import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../../../types';
import MoreVert from '../../icons/MoreVert';
import { avatarColor, initialOf } from '../../../common/avatar';
import { getPlanClass } from '../helpers';
import style from './CustomerListItem.module.css';

// Договір недійсний, якщо дата закінчення в минулому → показуємо номер червоним.
const isContractExpired = (finishAt?: string | null): boolean => {
  if (!finishAt) return false;
  const finish = new Date(finishAt).getTime();
  return !Number.isNaN(finish) && finish < Date.now();
};

const CalendarIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CopyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

interface CustomerListItemProps {
  client: Client;
  index: number;
  clientRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  handleClientMenuClick: (client: Client, index: number) => void;
  onRowClick: (client: Client) => void;
}

const CustomerListItem: React.FC<CustomerListItemProps> = ({
  client,
  index,
  clientRefs,
  handleClientMenuClick,
  onRowClick,
}) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const display = client.userName || '';

  const handleCopyEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!client.email) return;
    navigator.clipboard?.writeText(client.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Клік по номеру договору → сторінка Компанії з авто-відкриттям картки цієї компанії.
  const hasCompany = !!(client.companyName && client.companyOwnerId);
  const expired = isContractExpired(client.contractFinishAt);
  const handleContractClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!client.companyOwnerId) return;
    navigate(`/company/${client.companyOwnerId}`);
  };

  return (
    <div
      key={`${client.id}-${index}`}
      className={`${style.clientItem} ${
        index % 2 === 0 ? style.rowEven : style.rowOdd
      } ${index === clientRefs.current.length - 1 ? style.lastClientItem : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onRowClick(client)}
    >
      <div className={style.rowNum}>{index + 1}</div>
      <div className={style.inotify}>
        {client.avatarUrl ? (
          <img
            src={client.avatarUrl}
            alt={`${client.userName}'s avatar`}
            className={style.avatar}
          />
        ) : (
          <span
            className={style.avatarInitial}
            style={{ backgroundColor: avatarColor(display) }}
          >
            {initialOf(display)}
          </span>
        )}
        <div className={style.cellText}>
          <p className={style.primaryText}>{client.userName}</p>
        </div>
      </div>
      <div className={style.emailCell} data-label="Емейл">
        <span
          role="button"
          tabIndex={0}
          className={style.emailCopy}
          title="Натисніть, щоб скопіювати"
          onClick={handleCopyEmail}
        >
          {copied ? (
            <span className={style.copiedText}>✓ Скопійовано</span>
          ) : (
            <span className={style.secondaryText}>{client.email}</span>
          )}
          {!copied && client.email && <CopyIcon />}
        </span>
      </div>
      <div className={style.cellMid} data-label="Договір">
        {hasCompany ? (
          <button
            type="button"
            className={`${style.contractPill} ${
              expired ? style.contractExpired : ''
            }`}
            title={`Договір ${client.contractNumber || '—'} · ${
              client.companyName
            }${expired ? ' (недійсний)' : ''}. Перейти до картки компанії`}
            onClick={handleContractClick}
          >
            {client.contractNumber || '№ не вказано'}
          </button>
        ) : (
          <span className={`${style.planPill} ${getPlanClass(client.plan)}`}>
            Без договору
          </span>
        )}
      </div>
      <div className={style.cellMid} data-label="Дата реєстрації">
        <span className={style.termBadge}>
          <CalendarIcon />
          {client.createdAt
            ? new Date(client.createdAt).toLocaleDateString('uk-UA', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '—'}
        </span>
      </div>
      <a
        className={style.phoneLink}
        href={`tel:${client.phone}`}
        data-label="Телефон"
        onClick={(e) => e.stopPropagation()}
      >
        <PhoneIcon />
        <span className={style.tnum}>{client.phone}</span>
      </a>
      <button
        ref={(el) => (clientRefs.current[index] = el)}
        className={style.clientMenu}
        onClick={(e) => {
          e.stopPropagation();
          handleClientMenuClick(client, index);
        }}
      >
        <MoreVert />
      </button>
    </div>
  );
};

export default CustomerListItem;
